import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Users, Search, Edit2, CheckCircle2, User, FolderKanban, FileText, Save, X } from 'lucide-react';

export const AdminClientsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingClient, setEditingClient] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE' | 'PENDING'>('ACTIVE');

  const queryClient = useQueryClient();

  const { data: clientsRes, isLoading } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => api.get('/admin/clients').then((res) => res.data.data),
  });

  const updateMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.put(`/admin/clients/${id}`, { notes, status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setEditingClient(null);
    },
  });

  const clients = clientsRes || [];
  const filteredClients = clients.filter((c: any) => {
    const q = searchTerm.toLowerCase();
    return (
      c.user?.firstName?.toLowerCase().includes(q) ||
      c.user?.lastName?.toLowerCase().includes(q) ||
      c.user?.email?.toLowerCase().includes(q) ||
      c.companyName?.toLowerCase().includes(q)
    );
  });

  const startEdit = (c: any) => {
    setEditingClient(c);
    setNotes(c.notes || '');
    setStatus(c.status);
  };

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
          Client &amp; Author Directory
        </h1>
        <p className="text-xs sm:text-sm text-slate-400">
          Manage contracted publishing clients, author accounts, internal notes, and staff assignments.
        </p>
      </div>

      {/* Search */}
      <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search authors, imprints, or emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-brand-500"
          />
        </div>
        <span className="text-xs font-mono text-slate-400">
          Total: {clients.length} Clients
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading client directory...</div>
      ) : filteredClients.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-3">
          <Users className="w-12 h-12 text-slate-600 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-white">No Clients Found</h3>
        </div>
      ) : (
        <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-900/80 text-slate-400 font-mono text-[11px] uppercase border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4">Client / Author</th>
                  <th className="px-6 py-4">Organization / Imprint</th>
                  <th className="px-6 py-4">Assigned Staff</th>
                  <th className="px-6 py-4">Projects</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 font-sans">
                {filteredClients.map((c: any) => (
                  <tr key={c.id} className="hover:bg-slate-900/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-white">{c.user?.firstName} {c.user?.lastName}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{c.user?.email}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {c.companyName || 'Independent Author'}
                    </td>
                    <td className="px-6 py-4 font-mono text-[11px] text-slate-400">
                      {c.assignedStaff ? `${c.assignedStaff.firstName} ${c.assignedStaff.lastName}` : 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 font-mono font-bold text-brand-400">
                      {c.projectsCount || 0} Projects
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded text-[10px] font-mono font-bold uppercase ${
                        c.status === 'ACTIVE' ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-slate-900 text-slate-400'
                      }`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => startEdit(c)}
                        className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs inline-flex items-center gap-1 border border-slate-700"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        <span>Edit / Notes</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {editingClient && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-950 rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-800 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-serif text-xl font-bold text-white">
                Client Profile: {editingClient.user?.firstName} {editingClient.user?.lastName}
              </h3>
              <button onClick={() => setEditingClient(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Client Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Private Internal Staff Notes
                </label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes visible only to publishing staff..."
                  className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden focus:border-brand-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => setEditingClient(null)}
                className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={() => updateMutation.mutate(editingClient.id)}
                disabled={updateMutation.isPending}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs"
              >
                Save Client Notes
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
